import PropTypes from 'prop-types';
import Link from 'next/link';
import Layout from 'layouts/Default';
import { Section } from 'elements/Containers';
import HeroPost from 'components/HeroPost';
import PostCard from 'components/PostCard';
import Pagination from 'components/Pagination';
import BlogCta from 'components/BlogCta';
import Yoast from 'components/Yoast';
import BlogSubscription from 'components/BlogSubscription';

import { getAllPostsForHome, getPostCount, getSEOForHome } from 'lib/api';
import Canonical from 'components/Canonical';

const editorsPicks = [
  {
    title: 'Cold Emailing 101: How To Make More Money With Direct Outreach',
    href: '/blog/cold-emailing/',
    category: { name: 'Cold Emailing', href: '/category/cold-emailing' },
  },
  {
    title: 'The Definitive Guide to Cold Calling (Backed By Research and Data)',
    href: '/blog/cold-calling-tips/',
    category: { name: 'Cold Calling', href: '/category/cold-calling' },
  },
  {
    title: 'Follow-Up Emails: The Ultimate Guide (13 Proven Templates Inside)',
    href: '/blog/follow-up-email-guide/',
    category: { name: 'Follow-Up Email', href: '/category/follow-up-email' },
  },
  {
    title:
      'The Definitive Guide to B2B Sales in 2021 (Expert, Data-Backed Strategies)',
    href: '/blog/definitive-guide-to-b2b-sales/',
    category: { name: 'B2B Selling', href: '/category/b2b-selling' },
  },
  {
    title:
      'Sales Prospecting: How To Find Great Leads That Actually Turn Into Buyers',
    href: '/blog/sales-prospecting/',
    category: {
      name: 'Sales Prospecting',
      href: '/category/sales-prospecting',
    },
  },
];

const Blog = ({ posts: { edges }, seo, data: { pages } }) => {
  const heroPost = edges[0]?.node;
  const morePosts = edges.slice(1);

  return (
    <Layout>
      <Canonical />
      <Yoast seo={seo} />
      <Section>
        <div className="mx-auto mt-12 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
          <div className="p-6 lg:order-last">
            <h2 className="text-3xl font-semibold sm:text-4xl">
              Editor’s picks
            </h2>
            <div className="mt-3 sm:mt-4">
              <div className="grid gap-4">
                {editorsPicks.map((post) => (
                  <div key={post.title}>
                    <p className="text-sm font-medium text-blue-08">
                      <Link href={post.category.href} passHref>
                        <a className="hover:underline">{post.category.name}</a>
                      </Link>
                    </p>
                    <a className="mt-2 block" href={post.href}>
                      <p className="text-xl font-semibold">{post.title}</p>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <HeroPost
            id={heroPost.id}
            slug={heroPost.slug}
            featuredImage={heroPost.featuredImage}
            categories={heroPost.categories}
            title={heroPost.title}
            author={heroPost.author}
            date={heroPost.date}
          />
        </div>

        <BlogSubscription />

        <div className="mx-auto mt-5 grid max-w-lg gap-5 lg:mt-12 lg:max-w-none lg:grid-cols-3">
          {morePosts.map(({ node }) => (
            <PostCard
              key={node.id}
              id={node.id}
              slug={node.slug}
              featuredImage={node.featuredImage}
              categories={node.categories}
              title={node.title}
              author={node.author}
              date={node.date}
            />
          ))}
        </div>
        <Pagination currentPage={1} totalPages={pages} />
        <BlogCta />
      </Section>
    </Layout>
  );
};

Blog.propTypes = {
  posts: PropTypes.shape([]).isRequired,
  seo: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    posts: PropTypes.number.isRequired,
    pages: PropTypes.number.isRequired,
  }).isRequired,
};

export const getStaticProps = async () => {
  const { posts } = await getAllPostsForHome();
  const {
    page: { seo },
  } = await getSEOForHome();
  const data = await getPostCount();

  return {
    props: {
      posts,
      seo,
      data,
    },
    revalidate: 900,
  };
};

export default Blog;
